import { FormEvent, useState } from 'react'
import { Link } from 'react-router-dom'
import { forgotPasswordSchema } from '@fellowship/shared'
import { useForgotPassword } from '../hooks/useAuth'
import { Wordmark } from '../components/ui/Wordmark'

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState<string | undefined>()
  const forgotPassword = useForgotPassword()

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setEmailError(undefined)

    const parsed = forgotPasswordSchema.safeParse({ email })
    if (!parsed.success) {
      setEmailError(parsed.error.flatten().fieldErrors.email?.[0])
      return
    }

    forgotPassword.mutate(parsed.data)
  }

  if (forgotPassword.isSuccess) {
    return (
      <div className="min-h-screen bg-bg flex flex-col items-center justify-center px-5 py-10">
        <div className="mb-7"><Wordmark size={22} /></div>
        <div className="bg-surface border border-line-soft rounded-[20px] px-9 py-8 w-full max-w-[400px] text-center">
          <h2 className="serif text-[28px] tracking-tight m-0 mb-2.5">Check your inbox</h2>
          <p className="m-0 mb-5 text-[13px] text-ink-2 leading-[1.5]">
            If an account exists for <strong>{email}</strong>, we sent a reset link.
          </p>
          <Link to="/login" className="text-primary text-[13px] font-semibold no-underline">
            Back to log in
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-bg flex flex-col items-center justify-center px-5 py-10">
      <div className="mb-7"><Wordmark size={22} /></div>

      <div className="bg-surface border border-line-soft rounded-[20px] px-9 py-8 w-full max-w-[400px]">
        <h2 className="serif text-3xl tracking-tight m-0 mb-1.5">Reset password</h2>
        <p className="m-0 mb-6 text-[13px] text-ink-2">
          Enter your email and we'll send you a reset link.
        </p>

        <form onSubmit={handleSubmit} noValidate>
          <div className="mb-5 flex flex-col gap-1.5">
            <label htmlFor="email" className="text-xs font-semibold text-ink-2">
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input h-[46px]"
              aria-describedby={emailError ? 'email-error' : undefined}
              aria-invalid={Boolean(emailError)}
            />
            {emailError && (
              <div id="email-error" className="text-[11px] text-[#A33A2A]">{emailError}</div>
            )}
          </div>

          <button
            type="submit"
            disabled={forgotPassword.isPending}
            className="btn btn--full btn--lg"
          >
            {forgotPassword.isPending ? 'Sending…' : 'Send reset link'}
          </button>

          <p className="mt-4 text-center text-[13px] text-muted">
            <Link to="/login" className="text-primary font-semibold no-underline">
              Back to log in
            </Link>
          </p>
        </form>
      </div>
    </div>
  )
}
